import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { localNum, useStrictLocalization } from "@faire/web--source/common/localization/localize";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Span, Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IProduct } from "@faire/web-api--source/indigofair/data/IProduct";
import { IProductVariation } from "@faire/web-api--source/indigofair/data/IProductVariation";
import { IProductVariationMap } from "@faire/web-api--source/indigofair/data/IProductVariationMap";
import { observer } from "mobx-react";
import * as React from "react";
import { useMemo, useState } from "react";
import styled from "styled-components";
import { Chevron } from "@faire/retailer/icons/Chevron/Chevron";
import { titleCaseWithSpecialCharacters } from "@faire/retailer/lib/string";
import { OptionsRow } from "@faire/retailer/views/product/ProductDetails/AddToCart/OptionSelection/__internal__/SpecialOptionPicker/OptionsRow";
import { OptionVariation } from "@faire/retailer/views/product/ProductDetails/AddToCart/OptionSelection/__internal__/SpecialOptionPicker/OptionVariation";
import { getVariantDescriptorSuffix } from "./getVariantDescriptorSuffix";
import { SizeChartLink } from "./SizeChartLink";
// Show two rows of swatches at all times
export var NUM_ROWS_OF_SWATCHES = 2;
export var SWATCH_WIDTH = 48;
// Height of a single swatch in px
// Allows for animating showing more/less swatches
var SWATCH_ROW_TABLET_WIDTH = 300;
var SWATCH_ROW_DEFAULT_WIDTH = 360;
var DEFAULT_VARIATION_NAME = "default";
var TITLE_CASE_WITH_SPECIAL_CHARACTERS = titleCaseWithSpecialCharacters("Size");
var SpecialOptionPickerFn = function(param) {
    var variantOptionSelectionViewState = param.variantOptionSelectionViewState, variationName = param.variationName, variationDisplayType = param.variationDisplayType, alt = param.alt, brand = param.brand;
    var _useState = _sliced_to_array(useState(undefined), 2), hoveredValue = _useState[0], setHoveredValue = _useState[1];
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _useState1 = _sliced_to_array(useState(false), 2), showAllSwatches = _useState1[0], setShowAllSwatches = _useState1[1];
    var isTablet = useViewport().isTablet;
    var variationOptions = variantOptionSelectionViewState.variationsGroupedByName[variationName];
    var selectedVariationValue = variantOptionSelectionViewState.getSelectedVariationValue(variationName);
    var variationDisplayValue = hoveredValue || selectedVariationValue;
    var numSwatchesPerRow = Math.floor((isTablet ? SWATCH_ROW_TABLET_WIDTH : SWATCH_ROW_DEFAULT_WIDTH) / SWATCH_WIDTH);
    var _variationOptions_length;
    var totalNumRowsOfSwatches = Math.ceil(((_variationOptions_length = variationOptions === null || variationOptions === void 0 ? void 0 : variationOptions.length) !== null && _variationOptions_length !== void 0 ? _variationOptions_length : 0) / numSwatchesPerRow);
    var isSwatchPicker = variationDisplayType === IProductVariationMap.IEntry.VariationDisplayType.SWATCH;
    var _variationOptions_length1;
    var showExpandButton = isSwatchPicker && ((_variationOptions_length1 = variationOptions === null || variationOptions === void 0 ? void 0 : variationOptions.length) !== null && _variationOptions_length1 !== void 0 ? _variationOptions_length1 : 0) > numSwatchesPerRow * NUM_ROWS_OF_SWATCHES;
    var displayableName = !variationName || variationName.toLowerCase() === DEFAULT_VARIATION_NAME ? strictLocalize({
        id: "R0M65c",
        defaultMessage: [
            {
                type: 0,
                value: "Option"
            }
        ]
    }) : titleCaseWithSpecialCharacters(variationName);
    var _variationOptions_length2;
    var dropdownText = showAllSwatches ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "qCVd30",
        defaultMessage: [
            {
                type: 0,
                value: "Fewer colors"
            }
        ]
    }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "RvHlPd",
        defaultMessage: [
            {
                type: 1,
                value: "AdditionalSwatchOptionsNumber"
            },
            {
                type: 0,
                value: " more colors"
            }
        ],
        values: {
            AdditionalSwatchOptionsNumber: localNum(((_variationOptions_length2 = variationOptions === null || variationOptions === void 0 ? void 0 : variationOptions.length) !== null && _variationOptions_length2 !== void 0 ? _variationOptions_length2 : 0) - numSwatchesPerRow * 2)
        }
    });
    var selectMessage = useMemo(function() {
        if (hoveredValue === undefined) {
            return null;
        }
        var variation = IProductVariation.build({
            name: variationName,
            value: hoveredValue
        });
        var variationState = variantOptionSelectionViewState.stateForAlternateVariation(variation);
        var option = variantOptionSelectionViewState.optionForAlternateVariation(variation);
        // this method gets the HOVER message - i.e. if I'm hovering over an option
        // and it should be OOS, it should show OOS. For open sizing, we cannot simply
        // check for the single product option but the entire size variant to see
        // if it is purchasable (available/in stock)
        if (variantOptionSelectionViewState.isAlternateOpenSizingProduct(option) && variantOptionSelectionViewState.isAlternateOpenSizingAvailable(option) || variationState === IProduct.State.SALES_PAUSED) {
            return null;
        }
        var _getVariantDescriptorSuffix;
        return (_getVariantDescriptorSuffix = getVariantDescriptorSuffix(variationState, strictLocalize)) !== null && _getVariantDescriptorSuffix !== void 0 ? _getVariantDescriptorSuffix : null;
    }, [
        hoveredValue,
        variantOptionSelectionViewState,
        variationName,
        strictLocalize
    ]);
    var showSizeChartLink = !!brand.brand_size_chart && displayableName === TITLE_CASE_WITH_SPECIAL_CHARACTERS;
    var options = useMemo(function() {
        return variationOptions === null || variationOptions === void 0 ? void 0 : variationOptions.filter(function(variation) {
            return variation.value !== undefined;
        }).map(function(variation, index) {
            return /*#__PURE__*/ _jsx(OptionVariation, {
                productVariation: variation,
                index: index,
                viewState: variantOptionSelectionViewState,
                variationOptions: variationOptions,
                variationDisplayType: variationDisplayType,
                variationName: variationName !== null && variationName !== void 0 ? variationName : "",
                hoveredValue: hoveredValue,
                setHoveredValue: function(value) {
                    return setHoveredValue(value !== null && value !== void 0 ? value : undefined);
                },
                alt: alt !== null && alt !== void 0 ? alt : "",
                showAllSwatches: showAllSwatches
            }, index);
        });
    }, [
        alt,
        hoveredValue,
        showAllSwatches,
        variationDisplayType,
        variationName,
        variationOptions,
        variantOptionSelectionViewState
    ]);
    return /*#__PURE__*/ _jsxs(FullColumn, {
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                justify: "space-between",
                children: [
                    /*#__PURE__*/ _jsxs(Row, {
                        style: {
                            width: "100%"
                        },
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                truncate: true,
                                children: displayableName
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: 4
                            }),
                            /*#__PURE__*/ _jsxs(Typography, {
                                children: [
                                    variationDisplayValue,
                                    " ",
                                    /*#__PURE__*/ _jsx(Span, {
                                        color: Core.text.critical,
                                        style: {
                                            whiteSpace: "nowrap"
                                        },
                                        "data-test-id": "span-sop",
                                        children: selectMessage
                                    })
                                ]
                            })
                        ]
                    }),
                    showSizeChartLink ? /*#__PURE__*/ _jsx(Row, {
                        children: /*#__PURE__*/ _jsx(SizeChartLink, {
                            brand: brand,
                            product: variantOptionSelectionViewState.product
                        })
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsx(OptionsRow, {
                isSwatchPicker: isSwatchPicker,
                shouldShowAllSwatches: showAllSwatches,
                totalNumRowsOfSwatches: totalNumRowsOfSwatches,
                options: options
            }),
            showExpandButton ? /*#__PURE__*/ _jsx(DropdownButton, {
                onClick: function() {
                    return setShowAllSwatches(function(prev) {
                        return !prev;
                    });
                },
                children: /*#__PURE__*/ _jsxs(Typography, {
                    children: [
                        dropdownText,
                        " ",
                        /*#__PURE__*/ _jsx(Chevron, {
                            direction: showAllSwatches ? "up" : "down"
                        })
                    ]
                })
            }) : null,
            /*#__PURE__*/ _jsx(Spacer, {
                size: "3x"
            })
        ]
    });
};
export var SpecialOptionPicker = observer(SpecialOptionPickerFn);
var DropdownButton = styled.button.withConfig({
    componentId: "sc-597b3a0-0"
})([
    "padding:",
    ";text-align:left;"
], getSpacing("1x", 0));
var FullColumn = styled(Column).withConfig({
    componentId: "sc-597b3a0-1"
})([
    "width:100%;"
]);
